<template>
  <div>
    <p class="has-text-weight-bold is-size-4">
      <template v-if="!id">
        {{ $t("mp:user.create.title") }}
      </template>
      <template v-else> {{ user.lastName }} {{ user.firstName }}</template>
    </p>

    <div class="mp-card">
      <div class="is-flex mb-3">
        <p class="has-text-weight-bold is-size-5">
          {{ $t("mp:user.personal_data.title") }}
        </p>

        <template v-if="id">
          <div class="ml-auto">
            <b-button v-if="canReset" type="is-info" @click="resetUser"
              >{{ $t("mp:user.button.reset") }}
            </b-button>
          </div>

          <div class="ml-2">
            <b-button
              v-if="canLock && !user.locked"
              @click="lockUser"
              type="is-danger"
              >{{ $t("mp:user.button.lock") }}
            </b-button>

            <b-button
              v-if="canUnlock && user.locked"
              @click="unlockUser"
              type="is-danger"
              >{{ $t("mp:user.button.unlock") }}
            </b-button>
          </div>
        </template>
      </div>

      <template v-if="loaded">
        <form @submit.prevent="save">
          <validation-observer ref="observer">
            <div class="columns" :class="{ 'mt-5': !id }">
              <div class="column">
                <BInputWithValidation
                  :label="$t('mp:user.personal_data.last_name.label')"
                  rules="required"
                  label-position="on-border"
                  v-model="user.lastName"
                />
              </div>

              <div class="column">
                <BInputWithValidation
                  :label="$t('mp:user.personal_data.first_name.label')"
                  rules="required"
                  label-position="on-border"
                  v-model="user.firstName"
                />
              </div>
            </div>

            <div class="columns">
              <div class="column is-half">
                <BInputWithValidation
                  :label="$t('mp:user.personal_data.store.label')"
                  label-position="on-border"
                  v-model="user.store"
                />
              </div>
            </div>

            <p class="has-text-weight-bold is-size-5 mb-4">
              {{ $t("mp:profile.country.title") }}
            </p>

            <div class="columns mt-5">
              <div class="column">
                <CountrySelect
                  v-model="user.countryId"
                  :label="$t('mp:user.personal_data.language.label')"
                />
              </div>
            </div>

            <p class="has-text-weight-bold is-size-5 mb-4">
              {{ $t("mp:user.contact.title") }}
            </p>

            <div class="columns mt-5">
              <div class="column">
                <BInputWithValidation
                  :label="$t('mp:user.contact.email.label')"
                  rules="required|email"
                  label-position="on-border"
                  v-model="user.email"
                />
              </div>

              <div class="column">
                <PhoneInput
                  :label="$t('mp:user.contact.phone.label')"
                  v-model="user.phone"
                  :mask="
                    getUserCountry(user.countryId)
                      ? getUserCountry(user.countryId).phoneFormat
                      : ''
                  "
                  :prefix="
                    getUserCountry(user.countryId)
                      ? getUserCountry(user.countryId).phoneCountryCode
                      : ''
                  "
                />
              </div>
            </div>

            <div class="columns" :class="{ 'mt-5': !id }">
              <div class="column">
                <template v-if="canUpdateRoles">
                  <p class="has-text-weight-bold is-size-5 mb-5">
                    {{ $t("mp:user.roles.title") }}
                  </p>

                  <div class="is-flex is-flex-direction-column mb-6">
                    <b-checkbox v-model="user.admin" class="mb-4">
                      {{ $t("mp:user.roles.admin") }}
                    </b-checkbox>
                    <b-checkbox v-model="user.developer" class="mb-4">
                      {{ $t("mp:user.roles.developer") }}
                    </b-checkbox>
                    <b-checkbox v-model="user.user">
                      {{ $t("mp:user.roles.user") }}
                    </b-checkbox>
                  </div>
                </template>
              </div>
              <div class="column">
                <p class="has-text-weight-bold is-size-5 mb-5">
                  {{ $t("mp:user.auto.notifications.title") }}
                </p>
                <b-field>
                  <b-switch v-model="user.autoNotificationAtSuccessPrescore">
                    {{ $t("mp:user.auto.notification.at.success.prescore") }}
                  </b-switch>
                </b-field>
                <b-field>
                  <b-switch v-model="user.autoNotificationAtPrescore">
                    {{ $t("mp:user.auto.notification.at.prescore") }}
                  </b-switch>
                </b-field>
              </div>
            </div>
            <b-button
              native-type="submit"
              :loading="submitted"
              class="is-success"
              >{{ $t("mp:user.button.save") }}
            </b-button>
          </validation-observer>
        </form>
      </template>

      <LoadingSkeleton v-else />
    </div>
  </div>
</template>

<script>
import { AUTHORITIES } from "../utils/authorities";

import LoadingSkeleton from "../components/LoadingSkeleton";
import BInputWithValidation from "../components/input/BInputWithValidation";
import PhoneInput from "../components/input/PhoneInput";
import i18n from "../plugins/i18n";
import { ROLES } from "../utils/const";
import CountrySelect from "../components/input/CountrySelect.vue";
import { mapGetters } from "vuex/dist/vuex.common.js";

export default {
  name: "User",
  components: {
    PhoneInput,
    BInputWithValidation,
    LoadingSkeleton,
    CountrySelect,
  },
  title: "mp:page.user.title",
  props: {
    id: {
      type: String,
    },
  },
  data() {
    return {
      ROLES,
      user: {
        lastName: "",
        firstName: "",
        phone: "",
        email: "",
        countryId: null,
        roles: "",
        admin: false,
        developer: false,
        user: false,
        autoNotificationAtSuccessPrescore: false,
        autoNotificationAtPrescore: false,
      },
      loaded: false,
      submitted: false,
    };
  },
  async mounted() {
    if (this.id) {
      if (this.isSelf) {
        await this.$router.push({
          name: "Profile",
        });
        return;
      }
      await this.$store.dispatch("users/fetch", this.id);
      this.user = this.$store.getters["users/get"](this.id);
      this.parseRoles();

      if (this.user.roles.includes(ROLES.SUPER_ADMIN)) {
        await this.$store.dispatch("profile/fetch");
        let loggedInUser = this.$store.getters["profile/get"];
        if (!loggedInUser.roles.includes(ROLES.SUPER_ADMIN)) {
          await this.$router.push("/profile");
          return;
        }
      }
    } else {
      this.user.countryId = this.country.id;
    }

    this.loaded = true;
  },
  computed: {
    ...mapGetters({
      countries: "i18n/countries",
      country: "i18n/country",
    }),
    isSelf() {
      return this.$store.getters["session/userId"] === this.id;
    },
    canReset() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.USER.RESET,
        AUTHORITIES.USER.ADMIN,
      ]);
    },
    canLock() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.USER.LOCK,
        AUTHORITIES.USER.ADMIN,
      ]);
    },
    canUnlock() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.USER.UNLOCK,
        AUTHORITIES.USER.ADMIN,
      ]);
    },
    canUpdateRoles() {
      return this.$store.getters["session/hasAnyAuthority"]([
        AUTHORITIES.USER.UPDATE_ROLES,
        AUTHORITIES.USER.ADMIN,
      ]);
    },
  },
  methods: {
    async save() {
      const valid = await this.$refs.observer.validate();
      if (!valid) return;

      this.submitted = true;

      try {
        this.user.roles = this.canUpdateRoles ? this.serializeRoles() : null;

        if (this.id) {
          await this.$store.dispatch("users/update", {
            id: this.id,
            request: this.user,
          });
        } else {
          await this.$store.dispatch("users/create", this.user);
        }

        this.$buefy.toast.open({
          message: i18n.t("mp:user.save.success.message"),
          type: "is-success",
        });

        this.backToUsers();
      } catch (ex) {
        this.$buefy.toast.open({
          message: i18n.t("mp:user.save.failure.message"),
          type: "is-danger",
        });
      }

      this.submitted = false;
    },
    async resetUser() {
      this.$buefy.dialog.confirm({
        title: i18n.t("mp:user.reset_confirm.title"),
        message: i18n.t("mp:user.reset_confirm.message"),
        confirmText: i18n.t("mp:user.reset_confirm.confirm"),
        cancelText: i18n.t("mp:user.reset_confirm.cancel"),
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          try {
            await this.$store.dispatch("users/reset", this.id);
            this.$buefy.toast.open({
              message: i18n.t("mp:user.reset.success.message"),
              type: "is-success",
            });
          } catch (e) {
            console.error(e);
            this.$buefy.toast.open({
              message: i18n.t("mp:user.rest.failure.message"),
              type: "is-danger",
            });
          }
          this.backToUsers();
        },
      });
    },
    async lockUser() {
      this.$buefy.dialog.confirm({
        title: i18n.t("mp:user.lock_confirm.title"),
        message: i18n.t("mp:user.lock_confirm.message"),
        confirmText: i18n.t("mp:user.lock_confirm.confirm"),
        cancelText: i18n.t("mp:user.lock_confirm.cancel"),
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          try {
            await this.$store.dispatch("users/lock", {
              id: this.id,
              lock: true,
            });
            this.$buefy.toast.open({
              message: i18n.t("mp:user.lock.success.message"),
              type: "is-success",
            });
          } catch (e) {
            console.error(e);
            this.$buefy.toast.open({
              message: i18n.t("mp:user.lock.failure.message"),
              type: "is-danger",
            });
          }
          this.backToUsers();
        },
      });
    },
    async unlockUser() {
      this.$buefy.dialog.confirm({
        title: i18n.t("mp:user.unlock_confirm.title"),
        message: i18n.t("mp:user.unlock_confirm.message"),
        confirmText: i18n.t("mp:user.unlock_confirm.confirm"),
        cancelText: i18n.t("mp:user.unlock_confirm.cancel"),
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          try {
            await this.$store.dispatch("users/lock", {
              id: this.id,
              lock: false,
            });
            this.$buefy.toast.open({
              message: i18n.t("mp:user.unlock.success.message"),
              type: "is-success",
            });
          } catch (e) {
            console.error(e);
            this.$buefy.toast.open({
              message: i18n.t("mp:user.unlock.failure.message"),
              type: "is-danger",
            });
          }

          this.backToUsers();
        },
      });
    },
    parseRoles() {
      if (this.user.roles.includes(ROLES.ADMIN)) {
        this.user.admin = true;
      }
      if (this.user.roles.includes(ROLES.DEVELOPER)) {
        this.user.developer = true;
      }
      if (this.user.roles.includes(ROLES.USER)) {
        this.user.user = true;
      }
    },
    serializeRoles() {
      let roles = [];

      if (this.user.admin) {
        roles.push(ROLES.ADMIN);
      }
      if (this.user.developer) {
        roles.push(ROLES.DEVELOPER);
      }
      if (this.user.user) {
        roles.push(ROLES.USER);
      }

      return roles.join(",");
    },
    backToUsers() {
      this.$router.push({
        name: "Users",
      });
    },
    getUserCountry(countryId) {
      const country = this.countries.find(
        (country) => country.id === countryId
      );
      if (country) {
        country.phoneFormat = country.phoneFormat.replaceAll(/\d/g, "#");
      }
      return country;
    },
  },
};
</script>
